import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Trash2, Search, Wrench, FileText, Package, GripVertical, Type, FileSpreadsheet,
  Upload, AlertTriangle, Loader2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import MultiLineInput from "@/components/MultiLineInput";
import { buildLigneFromArticle } from "@/lib/articleToLigne";

/**
 * Composant partagé d'édition de lignes d'articles/prestations/textes.
 * Utilisé par Devis (DevisForm), Facturation (FactureCreate), et OR (OrDetail).
 *
 * Types de lignes :
 *   - libre        : champ libre (qty + PU + TVA)
 *   - article      : article catalogue (issu de GET /articles)
 *   - main_oeuvre  : heure de main d'œuvre (avec catégorie MO si tauxHoraires fourni)
 *   - forfait      : prestation forfaitaire
 *   - texte        : ligne de texte/section sans montant — n'entre PAS dans les totaux
 *
 * Drag & drop : HTML5 natif via attribut `draggable` (pas de dépendance).
 *
 * Props OBLIGATOIRES :
 *   - lignes (Array), setLignes (fn)
 *
 * Props OPTIONNELLES :
 *   - rabaisGlobal ({pct, chf}), setRabaisGlobal (fn)
 *   - showRabaisGlobal (bool, default true)
 *   - tvaDefaut (number, default 8.1) — utilisé pour les nouvelles lignes ET les lignes sans tva_taux
 *   - tvaField (string, default "tva_taux") — clé pour la TVA (utiliser "tva_pct" pour OR)
 *
 *   --- iter127 : EXTENSIONS POUR OR ---
 *   - tauxHoraires ({mecanique, carrosserie, diagnostic, pneus}) — si présent, les lignes MO affichent un sélecteur catégorie MO
 *   - extraToolbarButtons (React.Node) — slot pour boutons additionnels (ex: Importer un devis)
 *   - externalArticlePicker (bool, default false) — si true, n'affiche pas le picker interne et appelle onPickArticleClick(idx) à la place
 *   - onPickArticleClick (fn(idx|null)) — callback quand bouton "Article catalogue" ou icône loupe d'une ligne est cliqué
 *   - externalExcelImport (bool, default false) — si true, n'affiche pas le dialog Excel interne et appelle onExcelImportClick() à la place
 *   - onExcelImportClick (fn) — callback quand bouton "Importer Excel" est cliqué
 *   - moDefaults ({libelle, prix_unitaire_ht, unite, categorie_mo}) — valeurs par défaut quand on ajoute une ligne MO via le bouton
 */

const TYPE_LABELS = {
  libre: "Prestation libre",
  article: "Article",
  main_oeuvre: "MO",
  forfait: "Forfait",
  texte: "Texte",
};

const TYPE_ICONS = {
  libre: FileText,
  article: Package,
  main_oeuvre: Wrench,
  forfait: FileText,
  texte: Type,
};

const EMPTY_LIGNE = {
  libelle: "",
  quantite: 1,
  prix_unitaire_ht: 0,
  tva_taux: 8.1,
  unite: "",
  rabais_pct: 0,
  rabais_chf: 0,
  type_ligne: "libre",
  article_id: null,
};

export default function LignesEditor({
  lignes,
  setLignes,
  rabaisGlobal = { pct: 0, chf: 0 },
  setRabaisGlobal = () => {},
  showRabaisGlobal = true,
  tvaDefaut = 8.1,
  tvaField = "tva_taux",
  // iter127 — extensions OR
  tauxHoraires = null,
  extraToolbarButtons = null,
  externalArticlePicker = false,
  onPickArticleClick = null,
  externalExcelImport = false,
  onExcelImportClick = null,
  moDefaults = null,
}) {
  const [articlePickerOpen, setArticlePickerOpen] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Helper : lire la TVA d'une ligne (compat tva_taux / tva_pct)
  const getTva = (l) => parseFloat(l[tvaField] ?? l.tva_taux ?? l.tva_pct ?? tvaDefaut) || 0;

  const _newLocalId = () => (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const addLigne = (preset = {}) => {
    // iter127 — utilise tvaField pour respecter le naming OR (tva_pct) vs Devis/Facture (tva_taux)
    const newL = { ...EMPTY_LIGNE, _localId: _newLocalId(), ...preset };
    if (tvaField === "tva_pct") {
      newL.tva_pct = preset.tva_pct ?? preset.tva_taux ?? tvaDefaut;
      delete newL.tva_taux;
    } else {
      newL.tva_taux = preset.tva_taux ?? preset.tva_pct ?? tvaDefaut;
    }
    setLignes([...lignes, newL]);
  };

  const updateLigne = (idx, patch) => {
    setLignes(lignes.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const removeLigne = (idx) => {
    setLignes(lignes.filter((_, i) => i !== idx));
  };

  const onPickArticle = (article, idxToReplace = null) => {
    // iter165 — Support Article libre / MO forfait via helper unifié
    const preset = buildLigneFromArticle(article, { tvaField, tvaDefaut });
    // Article libre : ligne vide avec _localId
    if (article && article.__generic) {
      const line = { ...preset, _localId: _newLocalId() };
      if (tvaField === "tva_pct") delete line.tva_taux;
      if (idxToReplace !== null) {
        updateLigne(idxToReplace, line);
      } else {
        setLignes([...lignes, line]);
      }
      setArticlePickerOpen(false);
      setEditingIdx(null);
      return;
    }
    if (idxToReplace !== null) {
      updateLigne(idxToReplace, preset);
    } else {
      setLignes([...lignes, preset]);
    }
    setArticlePickerOpen(false);
    setEditingIdx(null);
  };

  // iter127 — wrapper pour le picker article (interne vs externe)
  const triggerArticlePicker = (idxToReplace = null) => {
    if (externalArticlePicker && onPickArticleClick) {
      onPickArticleClick(idxToReplace);
    } else {
      setEditingIdx(idxToReplace);
      setArticlePickerOpen(true);
    }
  };

  const triggerExcelImport = () => {
    if (externalExcelImport && onExcelImportClick) {
      onExcelImportClick();
    } else {
      setExcelOpen(true);
    }
  };

  // iter127 — preset pour les nouvelles lignes MO (taux horaire dynamique si fourni)
  const buildMoPreset = () => {
    if (moDefaults) return { type_ligne: "main_oeuvre", ...moDefaults };
    if (tauxHoraires) {
      return {
        type_ligne: "main_oeuvre",
        libelle: "Main d'œuvre",
        unite: "h",
        prix_unitaire_ht: tauxHoraires.mecanique || 150,
        categorie_mo: "mecanique",
      };
    }
    return { type_ligne: "main_oeuvre", libelle: "Main d'œuvre", unite: "h", prix_unitaire_ht: 150 };
  };

  // Drag & drop natif HTML5
  const onDragStart = (idx) => (e) => {
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    // Permet le drag sur Firefox
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* noop */ }
  };

  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };

  const onDragLeave = () => {
    setDragOverIdx(null);
  };

  const onDrop = (idx) => (e) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) {
      setDraggedIdx(null);
      setDragOverIdx(null);
      return;
    }
    const next = [...lignes];
    const [moved] = next.splice(draggedIdx, 1);
    next.splice(idx, 0, moved);
    setLignes(next);
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const onDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  // Calcul total ligne (HT avec rabais) — temps réel — exclut lignes "texte"
  const lineTotal = (l) => {
    if (l.type_ligne === "texte") return 0;
    const brut = (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire_ht) || 0);
    const rabais = brut * ((parseFloat(l.rabais_pct) || 0) / 100) + (parseFloat(l.rabais_chf) || 0);
    return Math.max(0, brut - rabais);
  };

  const sousTotal = lignes.reduce((acc, l) => acc + lineTotal(l), 0);
  const rg_pct = parseFloat(rabaisGlobal.pct) || 0;
  const rg_chf = parseFloat(rabaisGlobal.chf) || 0;
  const rabaisGlobalCalc = Math.min(sousTotal, sousTotal * (rg_pct / 100) + rg_chf);
  const totalHt = Math.max(0, sousTotal - rabaisGlobalCalc);
  const ratio = sousTotal > 0 ? totalHt / sousTotal : 0;
  const tva = lignes.reduce((acc, l) => {
    if (l.type_ligne === "texte") return acc;
    const netLigne = lineTotal(l) * ratio;
    return acc + netLigne * (getTva(l) / 100);
  }, 0);
  const totalTtc = totalHt + tva;

  // iter244 — un seul endroit définit les 4 boutons d'ajout (haut + rappel en bas)
  const AddLineButtons = ({ suffix, iconsOnly = false }) => {
    // iter254 — en bas de liste (rappel) on n'affiche que les icônes
    const lbl = (txt) => (iconsOnly ? null : <span className="hidden sm:inline">{txt}</span>);
    const ic = iconsOnly ? "w-3.5 h-3.5" : "w-3.5 h-3.5 sm:mr-1";
    return (
      <>
        <Button type="button" size="sm" variant="outline" onClick={() => triggerArticlePicker(null)} data-testid={`lignes-add-article${suffix}`} title="Article catalogue">
          <Package className={ic} />
          {lbl("Article catalogue")}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => addLigne()} data-testid={`lignes-add-libre${suffix}`} title="Prestation libre">
          <FileText className={ic} />
          {lbl("Prestation libre")}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => addLigne(buildMoPreset())} data-testid={`lignes-add-mo${suffix}`} title="Main d'œuvre">
          <Wrench className={ic} />
          {lbl("Main d'œuvre")}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => addLigne({ type_ligne: "texte", libelle: "", quantite: 0, prix_unitaire_ht: 0 })} data-testid={`lignes-add-texte${suffix}`} title="Texte / Section">
          <Type className={ic} />
          {lbl("Texte / Section")}
        </Button>
      </>
    );
  };

  return (
    <div className="space-y-3" data-testid="lignes-editor">
      {/* Toolbar — iter244 : imports en 1re ligne (une seule fois), ajouts en 2e ligne.
          Sur mobile les libellés sont masqués (icônes seules) pour gagner de la place. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="lignes-toolbar-imports">
          {extraToolbarButtons}
          <Button
            type="button" size="sm" variant="outline"
            onClick={triggerExcelImport}
            className="border-[#52a675]/30 text-[#3d8a5c] hover:bg-[#f0f9f4]"
            data-testid="lignes-add-excel"
            title="Importer un panier Excel (catalogue fournisseur)"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Importer Excel</span>
          </Button>
      </div>
      <div className="flex flex-wrap gap-2 pb-1" data-testid="lignes-toolbar-top">
        <AddLineButtons suffix="" />
      </div>

      {/* Lignes avec drag & drop */}
      <div className="md:border md:border-gray-200 md:rounded-lg md:overflow-hidden">
        {/* Header de table — visible uniquement md+ */}
        <div className="hidden md:grid bg-gray-50 text-xs uppercase text-gray-500 grid-cols-[28px_30px_1fr_70px_70px_90px_70px_70px_90px_36px] gap-1 px-2 py-2 border-b border-gray-200 items-center">
          {/* iter253 — les libellés sont décalés de la même valeur que le padding
              interne des champs (px-3) pour tomber pile au-dessus des valeurs. */}
          <div></div>
          <div></div>
          <div className="pl-3">Description</div>
          <div className="text-right pr-3">Qté</div>
          <div className="pl-3">Unité</div>
          <div className="text-right pr-3">PU HT</div>
          <div className="text-right pr-3">Rabais %</div>
          <div className="pl-3">TVA %</div>
          <div className="text-right pr-3">Total HT</div>
          <div></div>
        </div>
        <div className="space-y-2 md:space-y-0 md:divide-y md:divide-gray-100">
          {lignes.length === 0 && (
            <div className="text-center py-8 text-gray-400 italic text-sm border border-gray-200 rounded-lg md:border-0">
              Aucune ligne. Ajoutez avec les boutons ci-dessus.
            </div>
          )}
          {lignes.map((l, idx) => {
            const Icon = TYPE_ICONS[l.type_ligne || "libre"] || FileText;
            const isText = l.type_ligne === "texte";
            const isDragging = draggedIdx === idx;
            const isOverHere = dragOverIdx === idx && draggedIdx !== null && draggedIdx !== idx;
            return (
              <div
                key={l.id || l._localId || `row-${idx}`}
                draggable
                onDragStart={(e) => {
                  const tag = (e.target.tagName || "").toLowerCase();
                  if (["input", "textarea", "select", "button"].includes(tag) || e.target.isContentEditable) {
                    e.preventDefault();
                    return;
                  }
                  onDragStart(idx)(e);
                }}
                onDragOver={onDragOver(idx)}
                onDragLeave={onDragLeave}
                onDrop={onDrop(idx)}
                onDragEnd={onDragEnd}
                className={`transition-colors
                  ${isDragging ? "opacity-40" : ""}
                  ${isOverHere ? "md:bg-[#52a675]/10 md:border-t-2 md:border-[#52a675] ring-2 ring-[#52a675] md:ring-0" : ""}
                `}
                data-testid={`lignes-row-${idx}`}
              >
                {/* ─── VUE MOBILE (Card, <md) ─── */}
                <div className={`md:hidden p-3 space-y-3 rounded-lg border ${isText ? "bg-amber-50/40 border-amber-200" : "bg-white border-gray-200"}`}>
                  {/* Header : type + delete */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-600">
                      <GripVertical className="w-4 h-4 text-gray-400" data-testid={`lignes-drag-${idx}`} />
                      <Icon className={`w-4 h-4 ${isText ? "text-amber-600" : "text-gray-500"}`} />
                      <span className="text-xs uppercase font-semibold tracking-wide">
                        {TYPE_LABELS[l.type_ligne || "libre"] || "—"}
                      </span>
                    </div>
                    <Button
                      type="button" size="icon" variant="ghost"
                      onClick={() => removeLigne(idx)}
                      className="h-8 w-8 text-red-600 hover:bg-red-50"
                      data-testid={`lignes-delete-mobile-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {isText ? (
                    <Textarea
                      value={l.libelle || ""}
                      onChange={(e) => updateLigne(idx, { libelle: e.target.value })}
                      placeholder="Texte libre, titre de section, commentaire…"
                      rows={3}
                      className="text-sm bg-white"
                      style={{ fontSize: 16 }}
                      data-testid={`lignes-libelle-mobile-${idx}`}
                    />
                  ) : (
                    <>
                      {/* Description full width */}
                      <div>
                        <Label className="text-[10px] uppercase text-gray-500 mb-1 block">Description</Label>
                        <div className="flex gap-1">
                          <MultiLineInput
                            value={l.libelle || ""}
                            onChange={(e) => updateLigne(idx, { libelle: e.target.value })}
                            placeholder="Description"
                            className="min-h-10 text-sm leading-snug"
                            maxRows={8}
                            style={{ fontSize: 16 }}
                            data-testid={`lignes-libelle-mobile-${idx}`}
                          />
                          {l.type_ligne === "article" && (
                            <Button
                              type="button" size="icon" variant="outline"
                              className="h-10 w-10 flex-shrink-0"
                              onClick={() => triggerArticlePicker(idx)}
                              data-testid={`lignes-pick-mobile-${idx}`}
                            >
                              <Search className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Catégorie compta (libre only) */}
                      {l.type_ligne === "libre" && (
                        <div>
                          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">Compta</Label>
                          <Select
                            value={l.categorie_compta || "prestations"}
                            onValueChange={(v) => updateLigne(idx, { categorie_compta: v })}
                          >
                            <SelectTrigger className="h-10 text-sm" data-testid={`lignes-compta-mobile-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="prestations">📋 Prestations / MO (3400)</SelectItem>
                              <SelectItem value="marchandises">📦 Produit / Marchandise (3200)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* iter127 — Catégorie MO (uniquement si tauxHoraires fourni) */}
                      {l.type_ligne === "main_oeuvre" && tauxHoraires && (
                        <div>
                          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">Catégorie MO</Label>
                          <Select
                            value={l.categorie_mo || "mecanique"}
                            onValueChange={(v) => {
                              const taux = tauxHoraires?.[v];
                              updateLigne(idx, { categorie_mo: v, ...(taux ? { prix_unitaire_ht: taux } : {}) });
                            }}
                          >
                            <SelectTrigger className="h-10 text-sm" data-testid={`lignes-mo-cat-mobile-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mecanique">Mécanique</SelectItem>
                              <SelectItem value="carrosserie">Carrosserie</SelectItem>
                              <SelectItem value="diagnostic">Diagnostic</SelectItem>
                              <SelectItem value="pneus">Pneus</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* iter247 — compact : 3 champs par ligne (les quantités d'atelier
                          tiennent en 4 caractères, pas besoin d'une ligne chacune) */}
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">Qté</Label>
                          <Input
                            type="number" step="0.01" inputMode="decimal"
                            value={l.quantite ?? ""}
                            onChange={(e) => updateLigne(idx, { quantite: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                            onBlur={() => { if (l.quantite === "" || isNaN(l.quantite)) updateLigne(idx, { quantite: 0 }); }}
                            className="h-10 text-sm text-right px-2"
                            style={{ fontSize: 16 }}
                            data-testid={`lignes-qte-mobile-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">Unité</Label>
                          <Input
                            value={l.unite || ""}
                            onChange={(e) => updateLigne(idx, { unite: e.target.value })}
                            placeholder="h, pce…"
                            className="h-10 text-sm px-2"
                            style={{ fontSize: 16 }}
                            data-testid={`lignes-unite-mobile-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">PU HT</Label>
                          <Input
                            type="number" step="0.05" inputMode="decimal"
                            value={l.prix_unitaire_ht ?? ""}
                            onChange={(e) => updateLigne(idx, { prix_unitaire_ht: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                            onBlur={() => { if (l.prix_unitaire_ht === "" || isNaN(l.prix_unitaire_ht)) updateLigne(idx, { prix_unitaire_ht: 0 }); }}
                            className="h-10 text-sm text-right px-2"
                            style={{ fontSize: 16 }}
                            data-testid={`lignes-pu-mobile-${idx}`}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 items-end">
                        <div>
                          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">Rabais %</Label>
                          <Input
                            type="number" step="0.1" min="0" max="100" inputMode="decimal"
                            value={l.rabais_pct ?? 0}
                            onChange={(e) => updateLigne(idx, { rabais_pct: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                            onBlur={() => { if (l.rabais_pct === "" || isNaN(l.rabais_pct)) updateLigne(idx, { rabais_pct: 0 }); }}
                            className="h-10 text-sm text-right px-2"
                            style={{ fontSize: 16 }}
                            data-testid={`lignes-rabais-mobile-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">TVA</Label>
                          <Select value={String(getTva(l))} onValueChange={(v) => updateLigne(idx, { [tvaField]: parseFloat(v) })}>
                            <SelectTrigger className="h-10 text-sm px-2" data-testid={`lignes-tva-mobile-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="8.1">8.1%</SelectItem>
                              <SelectItem value="2.6">2.6%</SelectItem>
                              <SelectItem value="0">0%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="text-right">
                          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">Total HT</Label>
                          <div className="font-mono font-bold text-sm text-[#2d5f3f] h-10 flex items-center justify-end">
                            {lineTotal(l).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* ─── VUE DESKTOP (Grid, md+) ─── */}
                <div
                  className={`hidden md:grid grid-cols-[28px_30px_1fr_70px_70px_90px_70px_70px_90px_36px] gap-1 px-2 py-2 items-start text-sm
                    ${isText ? "bg-amber-50/40" : "hover:bg-gray-50/50"}
                  `}
                >
                  {/* Drag handle */}
                  <div className="flex items-center justify-center h-8 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600" title="Glisser pour réordonner" data-testid={`lignes-drag-desktop-${idx}`}>
                    <GripVertical className="w-4 h-4" />
                  </div>
                  {/* Type — emblème seul (iter261), libellé en infobulle */}
                  <div
                    className="flex items-center justify-center h-8"
                    title={TYPE_LABELS[l.type_ligne || "libre"] || ""}
                    data-testid={`lignes-type-icon-${idx}`}
                  >
                    <Icon className={`w-4 h-4 ${isText ? "text-amber-600" : "text-gray-400"}`} />
                  </div>

                  {isText ? (
                    <>
                      <div className="col-span-7">
                        <Textarea
                          value={l.libelle || ""}
                          onChange={(e) => updateLigne(idx, { libelle: e.target.value })}
                          placeholder="Texte libre, titre de section, commentaire… (n'entre pas dans les totaux)"
                          rows={2}
                          className="text-sm min-h-[40px] bg-white"
                          data-testid={`lignes-libelle-${idx}`}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Description + bouton pick article si type=article.
                          iter254 — tout reste dans UNE seule cellule de grille
                          (les <Select> compta/MO décalaient les colonnes suivantes). */}
                      <div className="min-w-0">
                      <div className="flex gap-1">
                        <MultiLineInput
                          value={l.libelle || ""}
                          onChange={(e) => updateLigne(idx, { libelle: e.target.value })}
                          placeholder="Description (Shift+Enter pour retour)"
                          className="min-h-8 text-sm"
                          data-testid={`lignes-libelle-${idx}`}
                        />
                        {l.type_ligne === "article" && (
                          <Button
                            type="button" size="icon" variant="outline"
                            className="h-8 w-8 flex-shrink-0"
                            onClick={() => triggerArticlePicker(idx)}
                            title="Changer d'article"
                            data-testid={`lignes-pick-${idx}`}
                          >
                            <Search className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                      {l.type_ligne === "libre" && (
                        <Select
                          value={l.categorie_compta || "prestations"}
                          onValueChange={(v) => updateLigne(idx, { categorie_compta: v })}
                        >
                          <SelectTrigger className="h-6 text-[10px] mt-1 w-full" data-testid={`lignes-compta-${idx}`} title="Choix comptable : marchandises (3200) ou prestations (3400)">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="prestations">📋 Prestations / MO (3400)</SelectItem>
                            <SelectItem value="marchandises">📦 Produit / Marchandise (3200)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {/* iter127 — Catégorie MO desktop (uniquement si tauxHoraires fourni) */}
                      {l.type_ligne === "main_oeuvre" && tauxHoraires && (
                        <Select
                          value={l.categorie_mo || "mecanique"}
                          onValueChange={(v) => {
                            const taux = tauxHoraires?.[v];
                            updateLigne(idx, { categorie_mo: v, ...(taux ? { prix_unitaire_ht: taux } : {}) });
                          }}
                        >
                          <SelectTrigger className="h-6 text-[10px] mt-1 w-full" data-testid={`lignes-mo-cat-${idx}`} title="Catégorie de main d'œuvre — applique le taux horaire">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mecanique">Mécanique</SelectItem>
                            <SelectItem value="carrosserie">Carrosserie</SelectItem>
                            <SelectItem value="diagnostic">Diagnostic</SelectItem>
                            <SelectItem value="pneus">Pneus</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      </div>
                      <Input
                        type="number" step="0.01"
                        value={l.quantite ?? ""}
                        onChange={(e) => updateLigne(idx, { quantite: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                        onBlur={() => { if (l.quantite === "" || isNaN(l.quantite)) updateLigne(idx, { quantite: 0 }); }}
                        className="h-8 text-sm text-right"
                        data-testid={`lignes-qte-${idx}`}
                      />
                      <Input
                        value={l.unite || ""}
                        onChange={(e) => updateLigne(idx, { unite: e.target.value })}
                        placeholder="h, pce…"
                        className="h-8 text-sm"
                        data-testid={`lignes-unite-${idx}`}
                      />
                      <Input
                        type="number" step="0.05"
                        value={l.prix_unitaire_ht ?? ""}
                        onChange={(e) => updateLigne(idx, { prix_unitaire_ht: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                        onBlur={() => { if (l.prix_unitaire_ht === "" || isNaN(l.prix_unitaire_ht)) updateLigne(idx, { prix_unitaire_ht: 0 }); }}
                        className="h-8 text-sm text-right"
                        data-testid={`lignes-pu-${idx}`}
                      />
                      <Input
                        type="number" step="0.1" min="0" max="100"
                        value={l.rabais_pct ?? 0}
                        onChange={(e) => updateLigne(idx, { rabais_pct: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                        onBlur={() => { if (l.rabais_pct === "" || isNaN(l.rabais_pct)) updateLigne(idx, { rabais_pct: 0 }); }}
                        className="h-8 text-sm text-right"
                        data-testid={`lignes-rabais-${idx}`}
                      />
                      <Select value={String(getTva(l))} onValueChange={(v) => updateLigne(idx, { [tvaField]: parseFloat(v) })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`lignes-tva-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="8.1">8.1%</SelectItem>
                          <SelectItem value="2.6">2.6%</SelectItem>
                          <SelectItem value="0">0%</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center justify-end h-8 pr-3 font-mono font-semibold text-sm">{lineTotal(l).toFixed(2)}</div>
                    </>
                  )}

                  {/* Delete */}
                  <div className="flex items-center justify-center h-8">
                    <Button
                      type="button" size="icon" variant="ghost"
                      onClick={() => removeLigne(idx)}
                      className="h-7 w-7 text-red-600 hover:bg-red-50"
                      data-testid={`lignes-delete-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* iter244 — Rappel des boutons d'ajout en bas, seulement quand la liste est
          assez longue pour que remonter soit pénible (≥ 4 lignes). */}
      {lignes.length >= 4 && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="lignes-toolbar-bottom">
          <AddLineButtons suffix="-bottom" iconsOnly />
        </div>
      )}

      {/* Rabais global + Totaux */}
      <div className="grid md:grid-cols-2 gap-4 pt-2">
        {showRabaisGlobal ? (
          <div className="space-y-2" data-testid="lignes-rabais-global">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Rabais global</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-[10px] text-gray-500">% sur sous-total</Label>
                <Input
                  type="number" step="0.1" min="0" max="100"
                  value={rabaisGlobal.pct || 0}
                  onChange={(e) => setRabaisGlobal({ ...rabaisGlobal, pct: parseFloat(e.target.value) || 0 })}
                  data-testid="lignes-rabais-global-pct"
                />
              </div>
              <div className="flex-1">
                <Label className="text-[10px] text-gray-500">CHF forfaitaire</Label>
                <Input
                  type="number" step="0.05" min="0"
                  value={rabaisGlobal.chf || 0}
                  onChange={(e) => setRabaisGlobal({ ...rabaisGlobal, chf: parseFloat(e.target.value) || 0 })}
                  data-testid="lignes-rabais-global-chf"
                />
              </div>
            </div>
          </div>
        ) : <div />}

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1 text-sm">
          {(rabaisGlobalCalc > 0 || lignes.some((l) => l.type_ligne !== "texte" && ((parseFloat(l.rabais_pct) || 0) > 0 || (parseFloat(l.rabais_chf) || 0) > 0))) && (
            <>
              <div className="flex justify-between text-gray-600">
                <span>Sous-total HT</span>
                <span className="font-mono">{sousTotal.toFixed(2)} CHF</span>
              </div>
              {rabaisGlobalCalc > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Rabais global</span>
                  <span className="font-mono">−{rabaisGlobalCalc.toFixed(2)} CHF</span>
                </div>
              )}
            </>
          )}
          <div className="flex justify-between">
            <span className="text-gray-700">Total HT</span>
            <span className="font-mono font-semibold">{totalHt.toFixed(2)} CHF</span>
          </div>
          <div className="flex justify-between text-gray-700">
            <span>TVA</span>
            <span className="font-mono">+ {tva.toFixed(2)} CHF</span>
          </div>
          <div className="flex justify-between text-base font-bold pt-1 border-t border-gray-300 text-[#2d5f3f]">
            <span>Total TTC</span>
            <span className="font-mono" data-testid="lignes-total-ttc">{totalTtc.toFixed(2)} CHF</span>
          </div>
        </div>
      </div>

      {!externalArticlePicker && (
        <ArticlePickerDialog
          open={articlePickerOpen}
          onClose={() => { setArticlePickerOpen(false); setEditingIdx(null); }}
          onPick={(a) => onPickArticle(a, editingIdx)}
        />
      )}
      {!externalExcelImport && (
        <LocalExcelImportDialog
          open={excelOpen}
          onClose={() => setExcelOpen(false)}
          onImport={(parsedLignes) => {
            const next = parsedLignes
              .filter((l) => (l.libelle || "").trim())
              .map((l) => {
                const tvaVal = parseFloat(l.tva_pct ?? l.tva_taux ?? tvaDefaut) || tvaDefaut;
                return {
                  ...EMPTY_LIGNE,
                  _localId: _newLocalId(),
                  libelle: (l.libelle || "").trim(),
                  quantite: parseFloat(l.quantite) || 1,
                  prix_unitaire_ht: parseFloat(l.prix_unitaire_ht) || 0,
                  [tvaField]: tvaVal,
                  // S'assurer qu'on n'a pas tva_taux=8.1 résiduel quand tvaField='tva_pct'
                  ...(tvaField === "tva_pct" ? { tva_taux: undefined } : {}),
                  unite: l.unite || "",
                  rabais_pct: parseFloat(l.rabais_pct) || 0,
                  rabais_chf: parseFloat(l.rabais_chf) || 0,
                  type_ligne: l.type_ligne || "article",
                  article_id: null,
                };
              });
            if (next.length === 0) return;
            setLignes([...lignes, ...next]);
            setExcelOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** Dialog pour piocher un article du catalogue. GET /articles.
 *  iter165 — En tête : "Article libre" (générique vide) + affichage du stock/alerte.
 */
function ArticlePickerDialog({ open, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/articles", { params: { q, actif_only: true, limit: 50 } });
        setItems(data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="article-picker-dialog">
        <DialogHeader>
          <DialogTitle>Choisir un article du catalogue</DialogTitle>
        </DialogHeader>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (référence, libellé, fournisseur)…"
            className="pl-10" autoFocus
            data-testid="article-picker-search"
          />
        </div>
        <div className="max-h-96 overflow-y-auto space-y-1">
          {/* iter165 — Article libre toujours en tête */}
          <button
            type="button"
            onClick={() => onPick({ __generic: true, id: "__generic__", libelle: "", categorie: "libre", unite: "", prix_ht: 0, tva: 8.1 })}
            className="w-full text-left p-2 rounded border-2 border-dashed border-[#52a675]/40 bg-[#f0f9f4]/50 hover:border-[#52a675] hover:bg-[#f0f9f4]"
            data-testid="article-picker-generic"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#52a675]" />
              <div>
                <div className="text-sm font-semibold text-[#3d8a5c]">Article libre</div>
                <div className="text-xs text-gray-500">Créer une ligne vide à remplir manuellement</div>
              </div>
            </div>
          </button>

          {loading && <div className="text-sm text-gray-400 text-center py-6">Recherche…</div>}
          {!loading && items.length === 0 && <div className="text-sm text-gray-400 text-center py-6">Aucun article trouvé</div>}
          {items.map((a) => {
            const hasStock = a.stock_quantite !== null && a.stock_quantite !== undefined;
            const low = hasStock && a.stock_seuil_alerte != null && a.stock_seuil_alerte > 0 && a.stock_quantite <= a.stock_seuil_alerte;
            return (
            <button
              key={a.id} type="button" onClick={() => onPick(a)}
              className="w-full text-left p-2 rounded border border-gray-100 hover:border-[#52a675] hover:bg-[#f0f9f4]"
              data-testid={`article-picker-pick-${a.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{a.libelle}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                    {a.reference && <code className="bg-gray-50 px-1 rounded">{a.reference}</code>}
                    <span>{a.categorie} {a.unite ? `· ${a.unite}` : ""}</span>
                    {hasStock && (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${low ? "bg-red-50 text-red-700 border border-red-200" : "bg-gray-50 text-gray-600 border border-gray-200"}`}>
                        {low && <AlertTriangle className="w-3 h-3" />}
                        stock : {a.stock_quantite}{a.stock_unite ? ` ${a.stock_unite}` : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="font-mono font-bold text-sm flex-shrink-0 ml-3">{(a.prix_ht || 0).toFixed(2)} CHF</div>
              </div>
            </button>
          );})}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * iter69 — Dialog d'import Excel SANS entité (stateless).
 *
 * Utilise POST /api/lignes/parse-excel pour parser le panier puis remet les
 * lignes au parent via `onImport(lignesArray)`. Le parent ajoute simplement à
 * son state local — pas de devis/facture créé tant que le formulaire n'est
 * pas soumis.
 */
function LocalExcelImportDialog({ open, onClose, onImport }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null); // { lignes, warnings, count }
  const [selected, setSelected] = useState({});
  const fileRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); setSelected({}); };
  const close = () => { reset(); onClose(); };

  const handleFile = async (f) => {
    setFile(f);
    setParsing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/lignes/parse-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      const all = {};
      (data.lignes || []).forEach((_, i) => { all[i] = true; });
      setSelected(all);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de lecture du fichier", { duration: 6000 });
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  const toggleAll = (val) => {
    const next = {};
    (preview?.lignes || []).forEach((_, i) => { next[i] = val; });
    setSelected(next);
  };

  const commit = () => {
    const keep = (preview?.lignes || []).filter((_, i) => selected[i]);
    if (keep.length === 0) {
      toast.error("Aucune ligne sélectionnée");
      return;
    }
    onImport(keep);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lignes-excel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#52a675]" /> Importer un panier Excel
          </DialogTitle>
          <DialogDescription>
            Fichier .xlsx avec colonnes : <strong>Description</strong> · <strong>Nombre</strong> · <strong>Prix cons.</strong>
            Les lignes parsées seront ajoutées au document en cours (non enregistré tant que vous ne soumettez pas le formulaire).
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#52a675] cursor-pointer"
            onClick={() => fileRef.current?.click()}
            data-testid="lignes-excel-dropzone"
          >
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <div className="text-sm text-gray-700 font-semibold">
              {parsing ? "Lecture du fichier…" : "Cliquez ou glissez le fichier .xlsx ici"}
            </div>
            {file && <div className="text-xs text-gray-500 mt-1">{file.name}</div>}
            <Input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              data-testid="lignes-excel-file-input"
            />
          </div>
        )}

        {parsing && (
          <div className="text-center py-6 text-sm text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Lecture en cours…
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            {(preview.warnings || []).length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                <div className="flex items-center gap-1 font-semibold mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Avertissements
                </div>
                <ul className="list-disc list-inside space-y-0.5">
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            <div className="flex items-center justify-between text-xs">
              <div className="text-gray-700 font-semibold">
                {preview.count || (preview.lignes || []).length} ligne(s) détectée(s)
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={() => toggleAll(true)} data-testid="lignes-excel-select-all">Tout cocher</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => toggleAll(false)} data-testid="lignes-excel-select-none">Tout décocher</Button>
              </div>
            </div>
            <div className="border border-gray-200 rounded max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase">
                  <tr>
                    <th className="p-1.5 w-8"></th>
                    <th className="text-left p-1.5">Libellé</th>
                    <th className="text-right p-1.5 w-14">Qté</th>
                    <th className="text-left p-1.5 w-14">Unité</th>
                    <th className="text-right p-1.5 w-20">PU HT</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.lignes || []).map((l, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-1.5">
                        <input
                          type="checkbox"
                          checked={!!selected[i]}
                          onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                          data-testid={`lignes-excel-row-${i}-check`}
                        />
                      </td>
                      <td className="p-1.5">{l.libelle}</td>
                      <td className="p-1.5 text-right font-mono">{l.quantite}</td>
                      <td className="p-1.5">{l.unite || "—"}</td>
                      <td className="p-1.5 text-right font-mono">{(parseFloat(l.prix_unitaire_ht) || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close}>Annuler</Button>
          {preview && (
            <Button onClick={commit} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="lignes-excel-commit">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Ajouter au document
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

